/**
 * Last-screen localStorage tracker · FS slot · 2026-05-31.
 *
 * Restores the user to whichever screen they were on after a hard reload.
 * ProgressLastSchema only carries `sprintId` + `ticketId` (sync schema), which
 * is enough to restore Sprint / Ticket but not Dashboard / Sprints Timeline /
 * Progress / Profile / etc.
 *
 * This is FE-local state (NOT synced across devices · cross-device users land
 * on their last sprint/ticket from progress.last, which IS synced). Adding
 * `screen` к ProgressLastSchema would require a shared-schemas migration via
 * `npm run sync:edge-shared` + main relay — pragmatic out-of-scope detour, so
 * we keep it browser-only for now. Tracked as CF-FS-PROGRESS-LAST-SCREEN if
 * cross-device screen restoration becomes a requirement.
 */

const KEY = 'qastudio.v1.last-screen';

/**
 * Whitelist of restorable screens. Welcome/auth deliberately excluded.
 *
 * `peek` is also excluded (24 JUN 2026 fix): it's the trial «заглянуть в спринт»
 * preview whose render depends on the transient `peekSprintId` state, which is NOT
 * persisted. Restoring to `peek` after a hard reload left `peekSprintId === null`,
 * so the screen rendered nothing — a blank page with only the global FABs. Keeping
 * it off the whitelist means a stored `last-screen: peek` (written before this fix)
 * is ignored on read and the user lands on the dashboard instead. RULE: only add a
 * screen here if it is fully restorable from persisted state alone (no transient
 * deps like peekSprintId).
 */
const RESTORABLE_SCREENS = [
  'dashboard',
  'sprint',
  'ticket',
  'sprints',
  'progress',
  'profile',
  'minilesson',
  'review',
  'finaltest',
  'mock',
  // slot_payments Phase C (11 JUL 2026): pricing is fully restorable (no transient deps).
  'pricing',
] as const;

export type RestorableScreen = (typeof RESTORABLE_SCREENS)[number];

export function writeLastScreen(screen: RestorableScreen): void {
  if (typeof window === 'undefined') return;
  // Runtime guard: App persists `screen as RestorableScreen`, so a non-restorable
  // screen (e.g. `peek`) can still reach here via the cast. Don't write it — a
  // stored non-restorable value would just be rejected on read anyway, and keeping
  // it out avoids leaving a stale `peek` in storage that looks restorable.
  if (!(RESTORABLE_SCREENS as readonly string[]).includes(screen)) return;
  try {
    window.localStorage.setItem(KEY, screen);
  } catch {
    /* quota · ignore */
  }
}

export function readLastScreen(): RestorableScreen | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return null;
    if ((RESTORABLE_SCREENS as readonly string[]).includes(raw)) {
      return raw as RestorableScreen;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearLastScreen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
